import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Package, ArrowRight } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { getMainInventory, updateMainInventoryQuantity, getSparePartTypes } from "@/integrations/supabase/inventory-services";
import AddToMainInventoryModal from "./AddToMainInventoryModal";
import TransferToBranchModal from "./TransferToBranchModal";
import { MainInventoryWithRelations } from "@/types/inventory";

const MainInventoryView = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MainInventoryWithRelations | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: inventory, isLoading } = useQuery<MainInventoryWithRelations[]>({
    queryKey: ['main-inventory'],
    queryFn: getMainInventory,
  });

  const { data: sparePartTypes } = useQuery({
    queryKey: ['spare-part-types'],
    queryFn: getSparePartTypes,
  });

  const updateQuantityMutation = useMutation({
    mutationFn: ({ id, quantity }: { id: string; quantity: number }) => 
      updateMainInventoryQuantity(id, quantity),
    onSuccess: () => {
      toast({
        title: "¡Éxito!",
        description: "Cantidad actualizada correctamente.",
      });
      queryClient.invalidateQueries({ queryKey: ['main-inventory'] });
    },
    onError: (error) => {
      console.error("Error updating quantity:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Error al actualizar la cantidad.",
      });
    },
  });

  const handleQuantityChange = (item: MainInventoryWithRelations, newQuantity: number) => {
    if (newQuantity >= 0) {
      updateQuantityMutation.mutate({ id: item.id, quantity: newQuantity });
    }
  };

  const handleTransfer = (item: MainInventoryWithRelations) => {
    setSelectedItem(item);
    setShowTransferModal(true);
  };

  if (isLoading) {
    return <div className="text-center py-4">Cargando inventario...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Stock Principal</h3>
        <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Agregar Repuesto
        </Button>
      </div>

      <div className="grid gap-4">
        {inventory?.map((item) => (
          <Card key={item.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package className="h-5 w-5 text-bike-600" />
                  <div>
                    <CardTitle className="text-lg">{item.spare_part_type?.name}</CardTitle>
                    {item.spare_part_type?.description && (
                      <p className="text-sm text-gray-600">{item.spare_part_type.description}</p>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTransfer(item)}
                  className="flex items-center gap-2"
                >
                  <ArrowRight className="h-4 w-4" />
                  Transferir
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium">Cantidad:</label>
                <Input
                  type="number"
                  min="0"
                  value={item.quantity}
                  onChange={(e) => handleQuantityChange(item, parseInt(e.target.value) || 0)}
                  className="w-32"
                />
                <span className="text-sm text-gray-500">unidades</span>
              </div>
            </CardContent>
          </Card>
        ))}

        {inventory?.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No hay repuestos en el inventario principal
          </div>
        )}
      </div>

      <AddToMainInventoryModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        sparePartTypes={sparePartTypes || []}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['main-inventory'] });
          setShowAddModal(false);
        }}
      />

      <TransferToBranchModal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setSelectedItem(null);
        }}
        inventoryItem={selectedItem}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['main-inventory'] });
          queryClient.invalidateQueries({ queryKey: ['branch-inventory'] });
          queryClient.invalidateQueries({ queryKey: ['inventory-transfers'] });
          setShowTransferModal(false);
          setSelectedItem(null);
        }}
      />
    </div>
  );
};

export default MainInventoryView;
